import React, { FC } from 'react';
import { Badge, Card, Heading, HStack, Icon, SimpleGrid, Stack, Text } from '@chakra-ui/react';
import { LuCheck, LuX } from 'react-icons/lu';
import { Button } from '@/components/ui/button';
import { PLANS, Plan, PlanTier } from '@/data/plans';

interface PricingCardsProps {
  onSelect: (tier: Exclude<PlanTier, 'Enterprise'>) => void;
  onContact?: () => void;
  currentTier?: string;
}

const PricingCards: FC<PricingCardsProps> = ({ onSelect, onContact, currentTier }) => {
  const handleCta = (plan: Plan) => {
    if (plan.cta === 'contact') {
      onContact?.();
      return;
    }
    onSelect(plan.tier as Exclude<PlanTier, 'Enterprise'>);
  };

  return (
    <SimpleGrid columns={{ base: 1, md: 3 }} gap='6' alignItems='stretch'>
      {PLANS.map((plan) => (
        <Card.Root
          key={plan.tier}
          variant={plan.highlighted ? 'elevated' : 'outline'}
          borderColor={plan.highlighted ? 'brand.400' : undefined}
          position='relative'
          h='full'
        >
          <Card.Body>
            <Stack gap='3'>
              {plan.highlighted && (
                <Badge colorPalette='green' alignSelf='flex-start'>
                  Most popular
                </Badge>
              )}
              <Text fontSize='sm' fontWeight='bold' textTransform='uppercase' color='brand.500'>
                {plan.name}
              </Text>
              <Text fontSize='sm' color='fg.muted'>
                {plan.tagline}
              </Text>
              <HStack alignItems='baseline' gap='1'>
                <Heading size='2xl'>{plan.priceLabel}</Heading>
                <Text fontSize='sm' color='fg.muted'>
                  {plan.priceSuffix}
                </Text>
              </HStack>
              <Text fontWeight='bold'>{plan.docsLabel}</Text>
              <Button
                data-testid={`cta-${plan.tier}`}
                colorScheme={plan.highlighted ? 'brand' : undefined}
                variant={plan.highlighted ? 'solid' : 'outline'}
                onClick={() => handleCta(plan)}
                disabled={!!currentTier && currentTier === plan.tier}
              >
                {currentTier === plan.tier ? 'Current plan' : plan.cta === 'contact' ? 'Contact sales' : 'Get started'}
              </Button>
              <Stack gap='2' mt='2'>
                {plan.features.map((f) => (
                  <HStack key={f.label} gap='2' alignItems='flex-start'>
                    <Icon color={f.included ? 'green.500' : 'gray.300'}>
                      {f.included ? <LuCheck /> : <LuX />}
                    </Icon>
                    <Text fontSize='sm' color={f.included ? 'fg' : 'fg.muted'}>
                      {f.label}
                    </Text>
                  </HStack>
                ))}
              </Stack>
            </Stack>
          </Card.Body>
        </Card.Root>
      ))}
    </SimpleGrid>
  );
};

export default PricingCards;
